import { defineStore } from 'pinia'

export interface MemberProfile {
  user_id: number
  username: string
  display_name: string
  avatar_url: string
  email: string
  level: number
  user_type: number
}

const profileStorageKey = 'goblog.member.profile'
const profileHintCookieKey = 'goblog_member_profile_hint'

// The access token remains in session storage. Keep this display-only profile
// beside it so an account layout can render immediately before refreshing it.
export const useMemberProfileStore = defineStore('member-profile', () => {
  const profile = ref<MemberProfile | null>(null)

  function restore() {
    if (!import.meta.client || profile.value) return
    try {
      const raw = window.sessionStorage.getItem(profileStorageKey)
      if (raw) profile.value = JSON.parse(raw) as MemberProfile
    } catch {
      window.sessionStorage.removeItem(profileStorageKey)
    }
  }

  function setProfile(value: MemberProfile) {
    profile.value = value
    if (import.meta.client) {
      window.sessionStorage.setItem(profileStorageKey, JSON.stringify(value))
      // This is display-only data for SSR navigation. Tokens stay in sessionStorage.
      const hint = JSON.stringify({ user_id: value.user_id, username: value.username, display_name: value.display_name, avatar_url: value.avatar_url })
      const secure = window.location.protocol === 'https:' ? '; Secure' : ''
      document.cookie = `${profileHintCookieKey}=${encodeURIComponent(hint)}; Path=/; SameSite=Lax${secure}`
    }
  }

  function clearProfile() {
    profile.value = null
    if (import.meta.client) {
      window.sessionStorage.removeItem(profileStorageKey)
      const secure = window.location.protocol === 'https:' ? '; Secure' : ''
      document.cookie = `${profileHintCookieKey}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
    }
  }

  return { profile, restore, setProfile, clearProfile }
})
